// Where a drawing aligned by hand lies on the first drawing: the point its centre is put on, its turn
// and its scale. The server takes it as a 2x3 matrix from the pixels of the drawing to those of the
// first drawing, the same kind of transform as automatic alignment finds (no stretching).

export interface Size {
    width: number;
    height: number;
}

export interface Placement {
    cx: number;
    cy: number;
    // degrees, clockwise on the screen
    angle: number;
    scale: number;
}

export type Matrix = number[][];

export interface Point {
    x: number;
    y: number;
}

const RADIANS = Math.PI / 180;

export function toMatrix(placement: Placement, drawing: Size): Matrix {
    const a = placement.scale * Math.cos(placement.angle * RADIANS);
    const b = placement.scale * Math.sin(placement.angle * RADIANS);
    const x = drawing.width / 2;
    const y = drawing.height / 2;
    return [
        [a, -b, placement.cx - (a * x - b * y)],
        [b, a, placement.cy - (b * x + a * y)],
    ];
}

export function fromMatrix(matrix: Matrix, drawing: Size): Placement {
    const [[a, c, e], [b, d, f]] = matrix;
    const x = drawing.width / 2;
    const y = drawing.height / 2;
    return {
        cx: a * x + c * y + e,
        cy: b * x + d * y + f,
        angle: Math.atan2(b, a) / RADIANS,
        scale: Math.hypot(a, b),
    };
}

// where a matrix puts a drawing; without one it lies at the top left corner at its own size: drawings
// next to each other share the sheet and the scale more often than not
export function startPlacement(drawing: Size, ...matrices: (Matrix | undefined)[]): Placement {
    return fromMatrix(matrices.find(Boolean) ?? [[1, 0, 0], [0, 1, 0]], drawing);
}

// the CSS transform of an element of the drawing's natural size, its origin at the top left corner,
// on the first drawing shown ``zoom`` times its natural size
export function cssTransform(placement: Placement, drawing: Size, zoom: number): string {
    const [[a, c, e], [b, d, f]] = toMatrix(placement, drawing);
    return `matrix(${[a, b, c, d, e, f].map((value) => value * zoom).join(', ')})`;
}

// degrees in [-180, 180)
export function wrapAngle(degrees: number): number {
    return ((((degrees + 180) % 360) + 360) % 360) - 180;
}

// the point of the drawing that ``placement`` puts on the point of the first drawing
export function toDrawing(placement: Placement, drawing: Size, point: Point): Point {
    const [[a, c, e], [b, d, f]] = toMatrix(placement, drawing);
    const det = a * d - b * c;
    const [x, y] = [point.x - e, point.y - f];
    return { x: (d * x - c * y) / det, y: (a * y - b * x) / det };
}

// the placement that puts the points of the drawing on those of the first drawing, two by two;
// null when the two points of either drawing lie on each other
export function placementFromPoints(drawing: Size, from: [Point, Point], to: [Point, Point]): Placement | null {
    const [dx, dy] = [from[1].x - from[0].x, from[1].y - from[0].y];
    const [cx, cy] = [to[1].x - to[0].x, to[1].y - to[0].y];
    const [length, target] = [Math.hypot(dx, dy), Math.hypot(cx, cy)];
    if (length < 1e-6 || target < 1e-6) return null;
    const scale = target / length;
    const turn = Math.atan2(cy, cx) - Math.atan2(dy, dx);
    // the centre of the drawing goes where the turn and the scale around the first point take it
    const [ox, oy] = [drawing.width / 2 - from[0].x, drawing.height / 2 - from[0].y];
    return {
        cx: to[0].x + scale * (ox * Math.cos(turn) - oy * Math.sin(turn)),
        cy: to[0].y + scale * (ox * Math.sin(turn) + oy * Math.cos(turn)),
        angle: wrapAngle(turn / RADIANS),
        scale,
    };
}
